import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VideoItem from "components/VideoItem"; // Import the new reusable component
import EnrollButton from "components/EnrollButton";

const CourseContent = ({
  contentDetails,
  loadedVideos,
  setLoadedVideos,
  activeVideo,
  setActiveVideo,
  videoPlayerRef,
  getVideoThumbnail,
  getEmbedUrl,
  isLoggedIn,
  authUser,
  navigate,
  location,
  setPendingPlan,
  plan,
}) => {
  const handlePlayVideo = (video) => {
    setActiveVideo(video);
    if (videoPlayerRef.current) {
      videoPlayerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 3,
          color: "primary.main",
          textAlign: "center",
          textTransform: "uppercase",
          letterSpacing: 1.5,
          borderBottom: (theme) => `2px solid ${theme.palette.primary.main}`,
          pb: 1,
          mx: "auto",
          width: "fit-content",
        }}
      >
        Course Content
      </Typography>
      {/* Video Player Section */}
      <Box ref={videoPlayerRef} sx={{ mb: activeVideo ? 3 : 0 }}>
        {activeVideo && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {activeVideo.videoTitle || "Untitled Video"}
            </Typography>
            <iframe
              width="100%"
              height="100%"
              src={`${getEmbedUrl(
                activeVideo.videoLink,
                activeVideo.provider
              )}?autoplay=1`}
              title={activeVideo.videoTitle || `${activeVideo.provider} Video`}
              frameBorder="0"
              allow={
                activeVideo.provider === "YOUTUBE"
                  ? "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  : "autoplay; fullscreen; picture-in-picture"
              }
              allowFullScreen
              style={{ borderRadius: "8px", height: "50vh" }}
            />
            {activeVideo.videoDescription && (
              <Typography variant="body2" sx={{ mt: 2, color: "#8595A6" }}>
                {activeVideo.videoDescription}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Accordion for Course Content */}
      {contentDetails &&
        contentDetails.map((section, sectionIndex) => (
          <Accordion
            key={sectionIndex}
            sx={{
              mb: 1,
              borderRadius: 2,
              "&::before": { height: 0 },
              "& .MuiAccordionSummary-content": { my: "20px" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: "background.default",
                borderRadius: 2,
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 500, m: 0 }}>
                {section.topic} ({section.contentDetails.length} lessons)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {section.contentDetails.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No videos available for this section.
                </Typography>
              ) : (
                section.contentDetails.map((content, idx) => (
                  <React.Fragment key={idx}>
                    <VideoItem
                      video={content}
                      index={idx}
                      sectionIndex={sectionIndex}
                      loadedVideos={loadedVideos}
                      setLoadedVideos={setLoadedVideos}
                      handlePlayVideo={handlePlayVideo}
                      getVideoThumbnail={getVideoThumbnail}
                      getEmbedUrl={getEmbedUrl}
                      isLoggedIn={isLoggedIn}
                      authUser={authUser}
                      navigate={navigate}
                      location={location}
                      setPendingPlan={setPendingPlan}
                      plan={plan}
                      showLockedState={true}
                    />
                    {idx < section.contentDetails.length - 1 && (
                      <Divider sx={{ my: 1 }} />
                    )}
                  </React.Fragment>
                ))
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      <Box sx={{ display: "flex", justifyContent: "end", mt: 2 }}>
        <EnrollButton
          plan={plan}
          isLoggedIn={isLoggedIn}
          authUser={authUser}
          navigate={navigate}
          location={location}
          setPendingPlan={setPendingPlan}
          width="auto"
        />
      </Box>
    </Box>
  );
};

export default CourseContent;
